import React, { useEffect, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { Link } from '~/lib/react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  GridLegacy as Grid,
  Stack,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';

import { ExperienceOffer, TimeGroups } from '~/services/ExperiencesService';

import { getPackageName } from '~/utils/experienceUtils';

import PageSubheader from '../../Common/Elements/PageSubheader';

type ExperienceOfferWithPackageNames = ExperienceOffer & {
  package_names: Array<string>;
  inactive: boolean;
};

interface Props {
  result: App.Offer;
  experiencesByPackageId: Record<string, Array<ExperienceOffer>>;
  experiencesTimes: Record<string, Array<TimeGroups>>;
}
export default function ComplimentaryExperiencesPane({ result, experiencesByPackageId }: Props) {
  const [groupedComplimentaryExperiences, setGroupedComplimentaryExperiences] = useState<
    Array<ExperienceOfferWithPackageNames>
  >([]);

  useEffect(() => {
    /*
      There's no direct link between an experience and its package, so we start by linking
      the packages to their experiences. Then, in the reducer, we collect all the package names
      for each experience and add them to the array in the main experience we're keeping.
    */
    const complimentaryExperiencesWithPackageNames = result.packages
      .flatMap((pkg) => {
        const experiences = experiencesByPackageId[pkg.id_salesforce_external] ?? [];
        return experiences.map((experience) => ({
          ...experience,
          inactive: experience.status === 'UNAVAILABLE',
          pkg,
        }));
      })
      .reduce<Array<ExperienceOfferWithPackageNames>>((acc, item) => {
        const existingExperience = acc.find((experience) => experience.id === item.id);

        if (existingExperience) {
          existingExperience.package_names.push(getPackageName(item.pkg));
        } else {
          acc.push({
            ...item,
            package_names: [getPackageName(item.pkg)],
          });
        }

        return acc;
      }, []);

    setGroupedComplimentaryExperiences(complimentaryExperiencesWithPackageNames);
  }, [result.packages, experiencesByPackageId]);

  return (
    <Box>
      <PageSubheader title="Complimentary Experiences" />
      {groupedComplimentaryExperiences.map((experience) => {
        return (
          <Accordion key={experience.id} variant="outlined">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                color: experience.inactive ? grey[600] : 'inherit',
              }}
            >
              {experience.inactive ? `(INACTIVE) ${experience.title}` : experience.title}
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Grid container>
                  <Grid item sm={3} mr={1}>
                    <Typography component="h6" color={grey[600]} sx={{ fontWeight: 500 }}>
                      DISPLAY NAME
                    </Typography>
                    <Typography variant="body1" color={grey[600]}>
                      <Link key={experience?.id} to={`/experience/${experience?.id}`} target="_blank">
                        {experience?.title || 'View Experience'}
                      </Link>
                    </Typography>
                  </Grid>
                  <Grid item sm={3}>
                    <Typography component="h6" color={grey[600]} sx={{ fontWeight: 500 }}>
                      VENDOR ID
                    </Typography>
                    <Typography variant="body1" color={grey[600]}>
                      {experience.vendor ? (
                        <Link to={`/vendors/${experience.vendor.id}`}>{experience.vendor.id}</Link>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs>
                    <Typography component="h6" color={grey[600]} sx={{ fontWeight: 500 }}>
                      DESCRIPTION
                    </Typography>
                    <Typography variant="body1" color={grey[600]}>
                      {experience.description}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs>
                    <Typography component="h6" color={grey[600]} sx={{ fontWeight: 500 }}>
                      COST PRICE
                    </Typography>
                    <Typography variant="body1" color={grey[600]}>
                      {experience.pricing ? (
                        <>
                          {experience.pricing.currency}{' '}
                          {currencyFormatter.format(experience.pricing.value, {
                            code: experience.pricing.currency,
                          })}
                        </>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs>
                    <Typography component="h6" color={grey[600]} sx={{ fontWeight: 500 }}>
                      AVAILABLE WITH PACKAGES
                    </Typography>
                    <Typography variant="body1" color={grey[600]}>
                      <ul>
                        {experience.package_names.map((name) => (
                          <li key={`${experience.id + name}`}>{name}</li>
                        ))}
                      </ul>
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
